import { getBackendUrl } from '@/lib/backendUrl';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, CheckCircle2, AlertCircle, KeyRound } from 'lucide-react';

const API = `${getBackendUrl()}/api`;

/**
 * Public invitation-acceptance page. Entered via the link in the invite
 * email: `/invite/:token`. Validates the token server-side, then lets the
 * recipient set a password and complete onboarding.
 */
export default function InviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState('validating'); // validating | ready | accepting | done | error
  const [user, setUser] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/invitations/validate`, { params: { token } });
        if (!cancelled) {
          setUser(data);
          setExpiresAt(data.expires_at);
          setState('ready');
        }
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || 'This invitation link is invalid or has expired.');
        setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setState('accepting');
    try {
      await axios.post(`${API}/invitations/accept`, { token, password });
      setState('done');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not activate account. Please try again or contact your administrator.');
      setState('ready');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="invite-accept-page">
      <Card className="w-full max-w-md shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-violet-600" /> Activate your account
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {state === 'validating' && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Checking invitation…
            </div>
          )}

          {state === 'error' && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 text-red-800 p-3 text-sm" data-testid="invite-error">
              <AlertCircle className="h-4 w-4 mt-0.5" />
              <div>
                <p>{error}</p>
                <p className="mt-2 text-xs">
                  If you have an activation code, you can still{' '}
                  <Link to="/activate" className="underline">enter it manually</Link>.
                </p>
              </div>
            </div>
          )}

          {(state === 'ready' || state === 'accepting') && user && (
            <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
              <div className="text-sm">
                <p className="text-muted-foreground">Signing up as</p>
                <p className="font-medium">{user.first_name} {user.last_name}</p>
                <p className="text-xs text-muted-foreground">{user.email}</p>
                {expiresAt && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Expires {new Date(expiresAt).toLocaleString()}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="new-password">New password</Label>
                <Input id="new-password" type="password" minLength={8}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="invite-password-input" autoComplete="new-password" required />
                <p className="text-xs text-muted-foreground mt-1">Minimum 8 characters.</p>
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm password</Label>
                <Input id="confirm-password" type="password" minLength={8}
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  data-testid="invite-confirm-input" autoComplete="new-password" required />
              </div>
              {error && (
                <div className="text-sm text-red-600" data-testid="invite-inline-error">{error}</div>
              )}
              <Button type="submit" className="w-full" disabled={state === 'accepting'} data-testid="invite-submit-btn">
                {state === 'accepting' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Activate account
              </Button>
            </form>
          )}

          {state === 'done' && (
            <div className="text-center space-y-3 py-4" data-testid="invite-done">
              <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
              <p className="font-medium">Account activated</p>
              <p className="text-sm text-muted-foreground">You can now sign in with your new password.</p>
              <Button onClick={() => navigate('/login')} data-testid="go-to-login-btn">Go to sign in</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
